/**
 * ScopeDAGNavigator — Cytoscape.js scope hierarchy visualization (DRR-075).
 *
 * Adapted from DAGVisualGraph.tsx with scope-specific styling:
 *   - Nodes = actors in scope hierarchy (Organization, Team, Patient, etc.)
 *   - Edges = containment relationships (contains, assigned_to, located_at)
 *   - Colors = scope depth level (root=dark, leaves=light)
 *   - Shapes: Subject=rectangle, Composite=hexagon, Human=ellipse
 *   - Click = select actor, show scope path + lens evaluation
 *
 * Features (constellation pattern):
 *   - Edge type filtering via clickable chips
 *   - Node filtering via excludeLeaves, excludePattern, includePattern props
 *   - Display names resolved from backend (not internal IDs)
 *
 * Uses breadthfirst layout (dagre plugin not available).
 */

import React, { useCallback, useEffect, useRef, useState, useMemo } from 'react';
import cytoscape, { type Core, type NodeSingular } from 'cytoscape';
import {
  Badge,
  Box,
  Chip,
  Group,
  Loader,
  Paper,
  Stack,
  Text,
  Title,
  Tooltip,
} from '@mantine/core';

import type { ScopeGraphData, ScopeEdge } from './hooks/useScopeGraph';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/** All supported edge types for filtering. */
const ALL_EDGE_TYPES = ['CONTAINS', 'ASSIGNED_TO', 'LOCATED_AT', 'PARTICIPATES', 'CORRELATES'] as const;
type EdgeTypeKey = typeof ALL_EDGE_TYPES[number];

/** Depth-based node colors (root=darkest, leaves=lightest). */
const DEPTH_COLORS = [
  '#1864ab', // depth 0 — Organization (darkest blue)
  '#1971c2', // depth 1 — Division
  '#228be6', // depth 2 — Facility
  '#339af0', // depth 3 — Unit / Team
  '#4dabf7', // depth 4 — Individual / Patient
  '#74c0fc', // depth 5+
];

/** Edge type colors. */
const EDGE_COLORS: Record<string, string> = {
  CONTAINS: '#868e96',
  ASSIGNED_TO: '#40c057',
  LOCATED_AT: '#fab005',
  PARTICIPATES: '#7950f2',
  CORRELATES: '#f06595',
};

/** Edge type labels for legend. */
const EDGE_LABELS: Record<string, string> = {
  CONTAINS: 'Contains',
  ASSIGNED_TO: 'Assigned To',
  LOCATED_AT: 'Located At',
  PARTICIPATES: 'Participates',
  CORRELATES: 'Correlates',
};

// ---------------------------------------------------------------------------
// Cytoscape Stylesheet
// ---------------------------------------------------------------------------

// Style objects use extended Cytoscape CSS properties — typed loosely like DAGVisualGraph
const SCOPE_STYLES = [
  // Base node
  {
    selector: 'node',
    style: {
      'background-color': '#228be6',
      'border-width': 2,
      'border-color': '#1864ab',
      label: 'data(label)',
      'text-valign': 'center',
      'text-halign': 'center',
      'font-size': 11,
      'font-weight': 'bold',
      color: '#fff',
      width: 70,
      height: 50,
      'text-wrap': 'wrap',
      'text-max-width': '80px',
      shape: 'round-rectangle',
    },
  },
  // Composite actors → hexagon
  {
    selector: 'node[actor_type="composite"]',
    style: { shape: 'hexagon', width: 75, height: 65 },
  },
  // Human actors → ellipse
  {
    selector: 'node[actor_type="human"]',
    style: { shape: 'ellipse', width: 60, height: 60 },
  },
  // Subject actors → rectangle (default shape)
  {
    selector: 'node[actor_type="subject"]',
    style: { shape: 'round-rectangle' },
  },
  // Root nodes — extra border
  {
    selector: 'node[?is_root]',
    style: {
      'border-width': 4,
      'border-color': '#0b3d6e',
      width: 85,
      height: 60,
    },
  },
  // Leaf nodes — dashed border
  {
    selector: 'node[?is_leaf]',
    style: { 'border-style': 'dashed' },
  },
  // Selection
  {
    selector: 'node:selected',
    style: {
      'border-width': 5,
      'border-color': '#f03e3e',
      'overlay-opacity': 0.15,
      'overlay-color': '#f03e3e',
    },
  },
  // Base edge
  {
    selector: 'edge',
    style: {
      width: 2,
      'line-color': '#adb5bd',
      'target-arrow-color': '#adb5bd',
      'target-arrow-shape': 'triangle',
      'curve-style': 'bezier',
      'arrow-scale': 1.2,
    },
  },
  // Contains edges (default)
  {
    selector: 'edge[edge_type="CONTAINS"]',
    style: { 'line-color': '#868e96', 'target-arrow-color': '#868e96' },
  },
  // Assigned-to edges
  {
    selector: 'edge[edge_type="ASSIGNED_TO"]',
    style: {
      'line-color': '#40c057',
      'target-arrow-color': '#40c057',
      'line-style': 'dashed',
    },
  },
  // Located-at edges
  {
    selector: 'edge[edge_type="LOCATED_AT"]',
    style: {
      'line-color': '#fab005',
      'target-arrow-color': '#fab005',
      'line-style': 'dotted',
    },
  },
  // Participates edges
  {
    selector: 'edge[edge_type="PARTICIPATES"]',
    style: {
      'line-color': '#7950f2',
      'target-arrow-color': '#7950f2',
      'line-style': 'dashed',
    },
  },
  // Correlates edges
  {
    selector: 'edge[edge_type="CORRELATES"]',
    style: {
      'line-color': '#f06595',
      'target-arrow-color': '#f06595',
      'line-style': 'dotted',
    },
  },
];

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface ScopeDAGNavigatorProps {
  /** Scope graph data from useScopeGraph hook. */
  graph: ScopeGraphData | null;
  /** Callback when a node (actor) is selected. */
  onNodeSelect?: (actorId: string) => void;
  /** Currently selected node ID. */
  selectedNodeId?: string | null;
  /** Scope path for the selected node. */
  scopePath?: string[];
  /** Container height in pixels. */
  height?: number;
  /**
   * Exclude leaf nodes (subjects like residents/patients).
   * Use for org structure views that shouldn't show care recipients.
   */
  excludeLeaves?: boolean;
  /**
   * Exclude specific node IDs by pattern (regex).
   * Example: /^Resident/ to exclude nodes starting with "Resident"
   */
  excludePattern?: RegExp;
  /**
   * Only show nodes matching this pattern (regex).
   * Takes precedence over excludePattern if both specified.
   */
  includePattern?: RegExp;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Infer actor type from node position in the graph. */
function inferActorType(
  actorId: string,
  roots: string[],
  leaves: string[]
): string {
  if (leaves.includes(actorId)) return 'subject';
  if (roots.includes(actorId)) return 'composite';
  return 'composite';
}

/** Calculate depth of each node via BFS from roots. */
function computeDepths(
  edges: ScopeEdge[],
  roots: string[]
): Map<string, number> {
  const depths = new Map<string, number>();
  const children = new Map<string, string[]>();

  for (const e of edges) {
    const list = children.get(e.sourceName) ?? [];
    list.push(e.targetName);
    children.set(e.sourceName, list);
  }

  const queue = [...roots];
  for (const r of roots) depths.set(r, 0);

  while (queue.length > 0) {
    const current = queue.shift()!;
    const d = depths.get(current) ?? 0;
    for (const child of children.get(current) ?? []) {
      if (!depths.has(child)) {
        depths.set(child, d + 1);
        queue.push(child);
      }
    }
  }

  return depths;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function ScopeDAGNavigator({
  graph,
  onNodeSelect,
  selectedNodeId,
  scopePath,
  height = 500,
  excludeLeaves = false,
  excludePattern,
  includePattern,
}: ScopeDAGNavigatorProps) {
  const cyRef = useRef<Core | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const [selectedNode, setSelectedNode] = useState<string | null>(
    selectedNodeId ?? null
  );

  // Edge type filter state — all enabled by default (constellation pattern)
  const [visibleEdgeTypes, setVisibleEdgeTypes] = useState<Set<EdgeTypeKey>>(
    new Set(ALL_EDGE_TYPES)
  );

  // Toggle edge type visibility
  const toggleEdgeType = useCallback((edgeType: EdgeTypeKey) => {
    setVisibleEdgeTypes((prev) => {
      const next = new Set(prev);
      if (next.has(edgeType)) {
        // Don't allow hiding all edge types
        if (next.size > 1) {
          next.delete(edgeType);
        }
      } else {
        next.add(edgeType);
      }
      return next;
    });
  }, []);

  // Helper: check if a node should be included based on filter props
  const shouldIncludeNode = useCallback(
    (nodeId: string, isLeaf: boolean): boolean => {
      // Exclude leaves if requested (e.g., org chart shouldn't show residents)
      if (excludeLeaves && isLeaf) return false;
      // Include pattern takes precedence
      if (includePattern) return includePattern.test(nodeId);
      // Exclude pattern
      if (excludePattern && excludePattern.test(nodeId)) return false;
      return true;
    },
    [excludeLeaves, excludePattern, includePattern]
  );

  // Compute which nodes are leaves (needed for filtering before edge filtering)
  const leafSet = useMemo(
    () => new Set(graph?.leaves ?? []),
    [graph?.leaves]
  );

  // Filter edges based on visible types AND node filters
  const filteredEdges = useMemo(() => {
    if (!graph?.edges) return [];
    return graph.edges.filter((e) => {
      // First: edge type filter
      if (!visibleEdgeTypes.has(e.edgeType as EdgeTypeKey)) return false;
      // Second: node filters — exclude edges where source or target would be hidden
      const sourceIsLeaf = leafSet.has(e.sourceName);
      const targetIsLeaf = leafSet.has(e.targetName);
      if (!shouldIncludeNode(e.sourceName, sourceIsLeaf)) return false;
      if (!shouldIncludeNode(e.targetName, targetIsLeaf)) return false;
      return true;
    });
  }, [graph?.edges, visibleEdgeTypes, leafSet, shouldIncludeNode]);

  // ---- Initialize Cytoscape -------------------------------------------
  useEffect(() => {
    if (!containerRef.current || cyRef.current) return undefined;

    try {
      cyRef.current = cytoscape({
        container: containerRef.current,
        style: SCOPE_STYLES as any,
        layout: {
          name: 'breadthfirst',
          directed: true,
          spacingFactor: 1.8,
          animate: true,
          animationDuration: 500,
        },
        minZoom: 0.1,
        maxZoom: 3,
        wheelSensitivity: 1,
      });

      // Node tap → select
      cyRef.current.on('tap', 'node', (event) => {
        const node = event.target as NodeSingular;
        const nodeId = node.id();
        setSelectedNode(nodeId);
        onNodeSelect?.(nodeId);
      });

      // Background tap → deselect (don't call onNodeSelect with empty string - causes GraphQL error)
      cyRef.current.on('tap', (event) => {
        if (event.target === cyRef.current) {
          setSelectedNode(null);
          // Don't call onNodeSelect with empty string - that causes "Scope node not found: " error
        }
      });

      setIsInitialized(true);
    } catch (err) {
      console.error('Failed to initialize Cytoscape:', err);
    }

    return () => {
      if (cyRef.current) {
        cyRef.current.destroy();
        cyRef.current = null;
      }
    };
  }, [onNodeSelect]);

  // ---- Update graph when data or filters change -----------------------
  useEffect(() => {
    if (!cyRef.current || !graph || !isInitialized) return;

    try {
      // Use full graph for depth computation (consistent layout), but filter for display
      const depths = computeDepths(graph.edges, graph.roots);

      // Collect unique node IDs from FILTERED edges only (hide orphan nodes when edge filtered out)
      const nodeIds = new Set<string>();
      for (const e of filteredEdges) {
        nodeIds.add(e.sourceName);
        nodeIds.add(e.targetName);
      }

      // Build Cytoscape elements
      const nodes = [...nodeIds].map((id) => {
        const depth = depths.get(id) ?? 0;
        const color = DEPTH_COLORS[Math.min(depth, DEPTH_COLORS.length - 1)];
        return {
          data: {
            id,
            label: id,
            actor_type: inferActorType(id, graph.roots, graph.leaves),
            depth,
            is_root: graph.roots.includes(id),
            is_leaf: graph.leaves.includes(id),
          },
          style: {
            'background-color': color,
            'border-color': DEPTH_COLORS[Math.max(0, Math.min(depth - 1, DEPTH_COLORS.length - 1))],
          },
        };
      });

      // Use filteredEdges for display
      const edges = filteredEdges.map((e, i) => ({
        data: {
          id: `edge-${i}`,
          source: e.sourceName,
          target: e.targetName,
          edge_type: e.edgeType,
        },
      }));

      // Remove old → add new → layout
      cyRef.current.elements().remove();
      cyRef.current.add(nodes as any);
      cyRef.current.add(edges as any);

      cyRef.current
        .layout({
          name: 'breadthfirst',
          directed: true,
          spacingFactor: 1.8,
          animate: true,
          animationDuration: 500,
        })
        .run();

      cyRef.current.fit(undefined, 40);
    } catch (err) {
      console.error('Failed to update scope graph:', err);
    }
  }, [graph, filteredEdges, isInitialized]);

  // ---- Sync external selection ----------------------------------------
  useEffect(() => {
    if (!cyRef.current || !selectedNodeId) return;
    try {
      cyRef.current.$(':selected').unselect();
      cyRef.current.$(`#${selectedNodeId}`).select();
      setSelectedNode(selectedNodeId);
    } catch (err) {
      console.error('Failed to select node:', err);
    }
  }, [selectedNodeId]);

  // ---- Fit-to-viewport callback ---------------------------------------
  const handleFitToView = useCallback(() => {
    cyRef.current?.fit(undefined, 40);
  }, []);

  // ---- Render ----------------------------------------------------------
  if (!graph) {
    return (
      <Paper p="md" withBorder>
        <Stack align="center" gap="sm">
          <Loader size="sm" />
          <Text size="sm" c="dimmed">
            Loading scope graph...
          </Text>
        </Stack>
      </Paper>
    );
  }

  if (graph.nodeCount === 0) {
    return (
      <Paper p="md" withBorder>
        <Text size="sm" c="dimmed" ta="center">
          No scope nodes configured. Add edges to build the scope hierarchy.
        </Text>
      </Paper>
    );
  }

  return (
    <Stack gap="sm">
      {/* Header */}
      <Group justify="space-between">
        <Title order={5}>Scope DAG</Title>
        <Group gap="xs">
          <Badge variant="light" color="blue" size="sm">
            {graph.nodeCount} nodes
          </Badge>
          <Badge
            variant="light"
            color={filteredEdges.length < graph.edgeCount ? 'orange' : 'gray'}
            size="sm"
          >
            {filteredEdges.length < graph.edgeCount
              ? `${filteredEdges.length} of ${graph.edgeCount} edges`
              : `${graph.edgeCount} edges`}
          </Badge>
          <Badge
            variant="light"
            color="teal"
            size="sm"
            style={{ cursor: 'pointer' }}
            onClick={handleFitToView}
          >
            Fit
          </Badge>
        </Group>
      </Group>

      {/* Edge type filter chips (constellation pattern — toggle to show/hide) */}
      <Group gap="xs">
        <Text size="xs" c="dimmed" fw={500}>Filter:</Text>
        <Chip.Group multiple>
          <Group gap="xs">
            {ALL_EDGE_TYPES.map((type) => {
              const isVisible = visibleEdgeTypes.has(type);
              const edgeCount = graph.edges.filter((e) => e.edgeType === type).length;
              if (edgeCount === 0) return null; // Don't show chip for edge types not present
              return (
                <Tooltip
                  key={type}
                  label={isVisible ? `Hide ${EDGE_LABELS[type]} edges` : `Show ${EDGE_LABELS[type]} edges`}
                  position="top"
                >
                  <Chip
                    size="xs"
                    variant={isVisible ? 'filled' : 'outline'}
                    checked={isVisible}
                    onChange={() => toggleEdgeType(type)}
                    color={EDGE_COLORS[type] ?? 'gray'}
                    styles={{
                      label: {
                        paddingLeft: 8,
                        paddingRight: 8,
                      },
                    }}
                  >
                    {EDGE_LABELS[type]} ({edgeCount})
                  </Chip>
                </Tooltip>
              );
            })}
          </Group>
        </Chip.Group>
        {visibleEdgeTypes.size < ALL_EDGE_TYPES.length && (
          <Badge
            size="xs"
            variant="light"
            color="blue"
            style={{ cursor: 'pointer' }}
            onClick={() => setVisibleEdgeTypes(new Set(ALL_EDGE_TYPES))}
          >
            Show All
          </Badge>
        )}
      </Group>

      {/* Cytoscape container */}
      <Box
        ref={containerRef}
        style={{
          width: '100%',
          height,
          border: '1px solid var(--mantine-color-gray-3)',
          borderRadius: 'var(--mantine-radius-sm)',
          background: 'var(--mantine-color-gray-0)',
        }}
      />

      {/* Selected node info */}
      {selectedNode && (
        <Paper p="sm" withBorder>
          <Stack gap="xs">
            <Group gap="xs">
              <Text size="sm" fw={600}>
                Selected:
              </Text>
              <Badge variant="filled" color="blue" size="sm">
                {selectedNode}
              </Badge>
            </Group>

            {scopePath && scopePath.length > 0 && (
              <Group gap={4}>
                <Text size="xs" c="dimmed">
                  Path:
                </Text>
                {scopePath.map((segment, i) => (
                  <React.Fragment key={segment}>
                    {i > 0 && (
                      <Text size="xs" c="dimmed">
                        /
                      </Text>
                    )}
                    <Badge
                      size="xs"
                      variant={segment === selectedNode ? 'filled' : 'light'}
                      color={segment === selectedNode ? 'red' : 'blue'}
                    >
                      {segment}
                    </Badge>
                  </React.Fragment>
                ))}
              </Group>
            )}
          </Stack>
        </Paper>
      )}
    </Stack>
  );
}
